import { useQuery } from "@tanstack/react-query"
import { ArrowRight, CalendarClock, CheckCircle2, Clock, FileSignature, FileText, Plus, Repeat, Timer, TrendingUp } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"

import { IconTile, Page, PageHeader, Panel, StatCard, StatGrid, Tag, fmtDateBR, type Tone } from "@/components/superadmin/kit"
import { SuperAdminLayout } from "@/components/superadmin/SuperAdminLayout"
import { Button } from "@/components/ui/button"
import { supabase } from "@/integrations/supabase/client"
import { STATUS_LABELS, fmtCurrency, propostaFromRow, resumoModalidade, totaisDosItens } from "@/lib/propostas"
import type { Proposta, StatusProposta } from "@/types/comercial"

const STATUS_TONE: Record<StatusProposta, Tone> = { rascunho: "neutral", enviada: "accent", aceita: "success", recusada: "danger", vencida: "warning" }

const MODULES = [
  { title: "Propostas", description: "Monte, envie e acompanhe o aceite", icon: FileText, url: "/gestao-comercial/propostas", table: "propostas" },
  { title: "Contratos", description: "Assinatura, vigência e modelos", icon: FileSignature, url: "/gestao-comercial/contratos", table: "contratos" },
  { title: "Reuniões e atas", description: "Agenda, pauta e atas com IA", icon: CalendarClock, url: "/gestao-comercial/reunioes", table: "reunioes_comerciais" },
  { title: "Horas técnicas", description: "Lançamentos cobráveis por projeto", icon: Timer, url: "/gestao-comercial/horas", table: "horas_tecnicas" },
] as const

function mensal(p: Proposta) {
  const mods = p.modalidades ?? []
  if (mods.length) return (mods.find((m) => m.id === p.modalidadeEscolhida) ?? mods.find((m) => m.recomendada) ?? mods[0]).valorMensal
  return totaisDosItens(p.itens).mensal
}

const GestaoComercial = () => {
  const navigate = useNavigate()
  const { data: proposals = [], isLoading } = useQuery({
    queryKey: ["commercial-overview-proposals"],
    queryFn: async () => {
      const { data, error } = await supabase.from("propostas").select("*").order("updated_at", { ascending: false })
      if (error) throw error
      return (data || []).map(propostaFromRow)
    },
  })

  const { data: moduleCounts = {} } = useQuery({
    queryKey: ["commercial-module-counts"],
    queryFn: async () => {
      const entries = await Promise.all(
        MODULES.map(async (m) => {
          const { count } = await supabase.from(m.table).select("*", { count: "exact", head: true })
          return [m.table, count ?? 0] as const
        }),
      )
      return Object.fromEntries(entries) as Record<string, number>
    },
  })

  const accepted = proposals.filter((p) => p.status === "aceita")
  const sent = proposals.filter((p) => p.status === "enviada")
  const decided = accepted.length + proposals.filter((p) => p.status === "recusada").length
  const conversion = decided ? Math.round((accepted.length / decided) * 100) : 0

  return (
    <SuperAdminLayout>
      <Page>
        <PageHeader
          title="Painel comercial"
          description="Pipeline de propostas, contratos, reuniões e horas da itaCondo."
          actions={
            <>
              <Button className="h-9 gap-1.5" onClick={() => navigate("/gestao-comercial/propostas?action=new")}><Plus className="h-4 w-4" />Nova proposta</Button>
            </>
          }
        />

        <StatGrid>
          <StatCard label="Em negociação" value={fmtCurrency(sent.reduce((t, p) => t + p.valorFinal, 0))} hint={`${sent.length} aguardando resposta`} icon={Clock} tone="accent" />
          <StatCard label="Fechado" value={fmtCurrency(accepted.reduce((t, p) => t + p.valorFinal, 0))} hint={`${accepted.length} proposta${accepted.length === 1 ? "" : "s"} aceita${accepted.length === 1 ? "" : "s"}`} icon={CheckCircle2} tone="success" />
          <StatCard label="Recorrência" value={fmtCurrency([...sent, ...accepted].reduce((t, p) => t + mensal(p), 0))} hint="mensal em aberto + fechado" icon={Repeat} tone="violet" />
          <StatCard label="Conversão" value={`${conversion}%`} hint={decided ? `${accepted.length} de ${decided} decididas` : "Nenhuma decidida ainda"} icon={TrendingUp} />
        </StatGrid>

        <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
          <Panel
            title="Pipeline"
            description="Propostas movimentadas por último"
            actions={<Link to="/gestao-comercial/propostas" className="text-[12px] font-medium text-[hsl(224_100%_76%)] hover:underline">Ver todas</Link>}
            flush
          >
            {isLoading ? (
              <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-[hsl(228_8%_10%)]" />)}</div>
            ) : proposals.length === 0 ? (
              <div className="px-4 py-10 text-center text-[13px] text-muted-foreground">Nenhuma proposta ainda.</div>
            ) : (
              <ul className="divide-y divide-border">
                {proposals.slice(0, 6).map((p) => {
                  const mods = p.modalidades ?? []
                  return (
                    <li key={p.id}>
                      <Link to="/gestao-comercial/propostas" className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[hsl(228_8%_10%)]">
                        <IconTile icon={FileText} tone={STATUS_TONE[p.status]} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-medium text-foreground">{p.titulo}</div>
                          <div className="truncate text-[12px] text-muted-foreground">{p.clienteEmpresa || p.clienteNome} · atualizada {fmtDateBR(p.updatedAt)}</div>
                        </div>
                        <div className="hidden text-right text-[13px] tabular-nums text-foreground sm:block">
                          {mods.length ? mods.map((m) => resumoModalidade(m)).join(" ou ") : fmtCurrency(p.valorFinal)}
                        </div>
                        <Tag tone={STATUS_TONE[p.status]} dot>{STATUS_LABELS[p.status]}</Tag>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            )}
          </Panel>

          <Panel title="Módulos" flush>
            <ul className="divide-y divide-border">
              {MODULES.map((m) => (
                <li key={m.url}>
                  <Link to={m.url} className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[hsl(228_8%_10%)]">
                    <IconTile icon={m.icon} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-foreground">{m.title}</div>
                      <div className="truncate text-[12px] text-muted-foreground">{m.description}</div>
                    </div>
                    <span className="text-[13px] tabular-nums text-muted-foreground">{moduleCounts[m.table] ?? "—"}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </Page>

    </SuperAdminLayout>
  )
}

export default GestaoComercial
export { GestaoComercial }
